import React, { Suspense, useEffect, useState, useCallback, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { ThemeProvider } from "@mui/material/styles";
import useKomutelTheme from "./hooks/use-komutel-theme";
import { Provider, useDispatch, useSelector } from "react-redux";
import store from "../rematch/store";
import { RematchRootState } from "@rematch/core";
import { KoreModel } from "static/rematch/models";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { LinearProgress, CssBaseline } from "@mui/material";
import translations from "Translation";
import axios from "axios";

const Desktop = React.lazy(() => import("./pages/desktop"));
const Login = React.lazy(() => import("./pages/login"));
const ResetPassword = React.lazy(() => import("./pages/resetPassword"));
const ChangePassword = React.lazy(() => import("./pages/changePasswordPage"));
const Error = React.lazy(() => import("./pages/error"));
const ConfigurationsV2 = React.lazy(() => import("./pages/configurationsV2"));
const { setLanguage, addTranslations, _ } = translations("Kore");

addTranslations(require.context("../../locale", true, /\.po$/));
setLanguage(navigator.language);

/* import whyDidYouRender from "@welldone-software/why-did-you-render";

whyDidYouRender(React, { trackAllPureComponents: true, collapseGroups: true, trackExtraHooks: [[require("react-redux/lib"), "useSelector"]] });*/

const Page = ({ title, component }) => {
  useEffect(() => {
    document.title = title;
  }, [title]);

  return <React.Fragment>{component}</React.Fragment>;
};

const ErrorPage = () => <Error title={_("403 Forbidden")} text={_("The page you requested cannot be accessed")} />;
const ValidateSSO = ({ ssoEnabled, children }) => (ssoEnabled ? <ErrorPage /> : children);

const Root = () => {
  const locale = useSelector((state: RematchRootState<KoreModel>) => state.sessionConfigs.locale);
  const [theme, logo] = useKomutelTheme(locale);
  const [ssoEnabled, setSsoEnabled] = useState();
  const dispatch = useDispatch();

  const toggleTheme = useCallback(() => {
    const newTheme = theme.palette.mode === "dark" ? "light" : "dark";

    localStorage.setItem("theme", newTheme);
    dispatch.desktopConfigs.updateData({ name: "theme", value: newTheme, save: false });
  }, [dispatch.desktopConfigs, theme.palette.mode]);

  const configsV2Component = useMemo(
    () => (
      <ValidateSSO ssoEnabled={ssoEnabled}>
        <Page component={<ConfigurationsV2 />} title={_("Settings") + " - Kore"} />
      </ValidateSSO>
    ),
    [ssoEnabled]
  );

  useEffect(() => {
    const getConfigs = async () => {
      dispatch.desktopConfigs.getData();

      try {
        const { data } = await axios.get("/sessions/ssoURL");

        setSsoEnabled(data.ssoURL);
      } catch (error) {
        console.log("Get ssoURL error", error);
      }
    };

    getConfigs();
  }, [dispatch.desktopConfigs]);

  return typeof ssoEnabled === "string" ? (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme>
        <Suspense fallback={<LinearProgress style={{ flexGrow: 1 }} />}>
          <BrowserRouter>
            <Routes>
              <Route path="/Desktop" element={<Page component={<Desktop logo={logo} />} title={_("Home") + " - Kore"} />} />
              <Route
                path="/Login"
                element={<Page component={<Login onThemeChange={toggleTheme} ssoEnabled={ssoEnabled} />} title={_("Login") + " - Kore"} />}
              />
              <Route
                path="/resetPassword"
                element={
                  <ValidateSSO ssoEnabled={ssoEnabled}>
                    <Page component={<ResetPassword onThemeChange={toggleTheme} />} title={_("Password reset") + " - Kore"} />
                  </ValidateSSO>
                }
              />
              <Route
                path="/passwordChange"
                element={
                  <ValidateSSO ssoEnabled={ssoEnabled}>
                    <Page component={<ChangePassword onThemeChange={toggleTheme} />} title={_("Change your password") + " - Kore"} />
                  </ValidateSSO>
                }
              />
              <Route path="/configurations" element={configsV2Component}>
                <Route path=":pluginName" element={configsV2Component}>
                  <Route index element={configsV2Component} />
                  <Route path=":pluginId" element={configsV2Component}>
                    <Route index element={configsV2Component} />
                    <Route path=":subPluginName" element={configsV2Component}>
                      <Route index element={configsV2Component} />
                      <Route path=":subPluginId" element={configsV2Component} />
                    </Route>
                  </Route>
                </Route>
              </Route>
              <Route path="/forbidden" element={<ErrorPage />} />
              <Route
                path="/*"
                element={
                  <Page
                    component={<Error onThemeChange={toggleTheme} title={_("404 Not found")} text={_("The page you requested cannot be found")} />}
                    title={_("404 Not found") + " - Kore"}
                  />
                }
              />
            </Routes>
          </BrowserRouter>
        </Suspense>
      </CssBaseline>
    </ThemeProvider>
  ) : null;
};

const RootProvider = () => (
  <Provider store={store}>
    <Root />
  </Provider>
);

const rootElement = document.getElementById("root");
const root = createRoot(rootElement);

root.render(<RootProvider />);
